import React, { useState } from 'react';
import { CloseIcon, DeleteIcon } from './icons';

export const TodoPanel = ({ 
  nodes = [], 
  onClose,
  onUpdateNode
}) => {
  // Extract TODO items from TODO_LIST nodes
  const todoItems = nodes
    .filter(node => node.type === 'TODO_LIST')
    .flatMap(node => 
      (node.data.items || []).map((item, index) => ({
        id: `${node.id}-${index}`,
        nodeId: node.id,
        nodeLabel: node.label,
        text: item.text || item,
        completed: item.completed || false,
        priority: node.data.priority || 'medium',
        assignedTo: item.assignedTo || node.data.assignedTo || 'Unassigned',
        deadline: item.deadline || ''
      }))
    )
    .sort((a, b) => {
      // Sort by completion status (incomplete first) then by priority
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      
      // Sort by priority (high > medium > low)
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

  const [newTodoText, setNewTodoText] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState(
    nodes.find(node => node.type === 'TODO_LIST')?.id || ''
  );

  const handleToggleTodo = (todoId, nodeId, completed) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    const todoIndex = parseInt(todoId.split('-').pop());
    const updatedItems = [...(node.data.items || [])];
    
    if (typeof updatedItems[todoIndex] === 'string') {
      updatedItems[todoIndex] = {
        text: updatedItems[todoIndex],
        completed: !completed
      };
    } else {
      updatedItems[todoIndex] = {
        ...updatedItems[todoIndex],
        completed: !completed
      };
    }
    
    onUpdateNode(nodeId, {
      data: {
        ...node.data,
        items: updatedItems
      }
    });
  };

  const handleAddTodo = () => {
    if (!newTodoText.trim() || !selectedNodeId) return;
    
    const node = nodes.find(n => n.id === selectedNodeId);
    if (!node) return;
    
    const updatedItems = [
      ...(node.data.items || []),
      {
        text: newTodoText.trim(),
        completed: false
      }
    ];
    
    onUpdateNode(selectedNodeId, {
      data: {
        ...node.data,
        items: updatedItems
      }
    });
    
    setNewTodoText('');
  };

  const handleDeleteTodo = (nodeId, index) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    const updatedItems = [...(node.data.items || [])];
    updatedItems.splice(index, 1);
    
    onUpdateNode(nodeId, {
      data: {
        ...node.data,
        items: updatedItems
      }
    });
  };

  const todoListNodes = nodes.filter(node => node.type === 'TODO_LIST');

  return (
    <div className="absolute top-16 right-4 bg-dark-surface border border-dark-border rounded-lg shadow-xl z-40 w-80">
      <div className="p-4 border-b border-dark-border flex items-center justify-between">
        <h3 className="font-bold text-lg text-dark-text-primary">To-Do List</h3>
        <button 
          onClick={onClose}
          className="p-1 rounded-md text-dark-text-secondary hover:bg-dark-bg"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>
      
      <div className="p-4 max-h-96 overflow-y-auto">
        {todoListNodes.length === 0 ? (
          <div className="text-center py-8 text-dark-text-secondary">
            <div className="mx-auto w-12 h-12 bg-dark-bg rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-sm">No TODO_LIST nodes found in your workflow</p>
            <p className="text-xs mt-1">Add a TODO_LIST node to start tracking tasks</p>
          </div>
        ) : (
          <>
            {/* Add new todo */}
            <div className="mb-6">
              <div className="flex gap-2 mb-2">
                <select
                  value={selectedNodeId}
                  onChange={(e) => setSelectedNodeId(e.target.value)}
                  className="flex-1 bg-dark-bg border border-dark-border rounded-md px-2 py-1 text-sm text-dark-text-primary"
                >
                  {todoListNodes.map(node => (
                    <option key={node.id} value={node.id}>
                      {node.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTodoText}
                  onChange={(e) => setNewTodoText(e.target.value)}
                  placeholder="Add a new task..."
                  className="flex-1 bg-dark-bg border border-dark-border rounded-md px-3 py-2 text-sm text-dark-text-primary"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTodo()}
                />
                <button
                  onClick={handleAddTodo}
                  className="bg-brand-primary text-white rounded-md px-3 py-2 text-sm hover:bg-brand-primary/90"
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Todo items */}
            <div className="space-y-2">
              {todoItems.length === 0 ? (
                <div className="text-center py-4 text-dark-text-secondary">
                  <p className="text-sm">No tasks yet</p>
                  <p className="text-xs mt-1">Add tasks to your TODO_LIST nodes</p>
                </div>
              ) : (
                todoItems.map(todo => (
                  <div 
                    key={todo.id}
                    className={`flex items-start gap-2 p-3 rounded-md ${
                      todo.completed 
                        ? 'bg-dark-bg/50' 
                        : 'bg-dark-bg hover:bg-dark-surface'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={todo.completed}
                      onChange={() => handleToggleTodo(todo.id, todo.nodeId, todo.completed)}
                      className="mt-1 rounded text-brand-primary focus:ring-brand-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${todo.completed ? 'line-through text-dark-text-secondary' : 'text-dark-text-primary'}`}>
                        {todo.text}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          todo.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                          todo.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {todo.priority}
                        </span>
                        <span className="text-xs text-dark-text-secondary truncate">
                          {todo.nodeLabel}
                        </span>
                      </div>
                      {todo.deadline && (
                        <div className="text-xs text-dark-text-tertiary mt-1">
                          Due: {todo.deadline}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteTodo(todo.nodeId, parseInt(todo.id.split('-').pop()))}
                      className="p-1 text-dark-text-secondary hover:text-red-400"
                    >
                      <DeleteIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
      
      {todoItems.length > 0 && (
        <div className="p-4 border-t border-dark-border text-xs text-dark-text-secondary">
          <div className="flex justify-between">
            <span>
              {todoItems.filter(t => !t.completed).length} pending tasks
            </span>
            <span>
              {todoItems.filter(t => t.completed).length} completed
            </span>
          </div>
        </div>
      )}
    </div>
  );
};